import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";

import {
  loadRuleCatalog,
  selectRuleCatalog,
} from "../../../Redux/Reducers/ruleCatalogReducer";
import {
  RULE_CATEGORY_LABELS,
  RULE_CATEGORY_ORDER,
  rulesApi,
} from "../../../utils/rulesApi";
import {
  draftFromForm,
  emptyForm,
  formFromRule,
  isFormComplete,
  typeLabel,
  validateForm,
} from "../../../utils/ruleDraft";
import { useRuleStyles } from "../styles";
import RuleFieldsForm from "./RuleFieldsForm";

export const PREVIEW_DELAY_MS = 350;

export const MODES = {
  ADD: "add",
  REPLACE: "replace",
  EVERYWHERE: "everywhere",
};

const IDLE_PREVIEW = { data: null, loading: false, error: null };

/**
 * Three-step dialog: kind of rule → rule type → scope and values, with a debounced
 * `POST /preview` that supplies the text, the field errors and the duplicate check.
 *
 * - `add`: picks a new rule for the set; the result is `{ ruleId | null, draft, text, … }`.
 * - `replace`: edits a row; by default it swaps the row for a find-or-create of the new values.
 *   When the stored rule is used by this set alone, "update the rule itself" is offered too.
 * - `everywhere`: PUT on the stored rule across every set, after the 409 confirmation.
 */
const AddRuleDialog = ({
  open,
  mode,
  initial,
  setName,
  totalDives,
  onClose,
  onApply,
}) => {
  const classes = useRuleStyles();
  const dispatch = useDispatch();
  const catalog = useSelector(selectRuleCatalog);

  const [category, setCategory] = useState(null);
  const [typeKey, setTypeKey] = useState(null);
  const [form, setForm] = useState(emptyForm(null));
  const [preview, setPreview] = useState(IDLE_PREVIEW);
  const [inPlace, setInPlace] = useState(false);
  const [affected, setAffected] = useState(null);
  const [busy, setBusy] = useState(false);
  const requestId = useRef(0);

  const typesByKey = useMemo(
    () => Object.fromEntries(catalog.types.map((t) => [t.key, t])),
    [catalog.types],
  );
  const type = typeKey ? typesByKey[typeKey] : null;
  const locked = mode !== MODES.ADD;

  useEffect(() => {
    if (open) dispatch(loadRuleCatalog());
  }, [open, dispatch]);

  // Reset on open (and once the catalog arrives, when editing).
  useEffect(() => {
    if (!open) return;
    requestId.current += 1;
    setPreview(IDLE_PREVIEW);
    setAffected(null);
    setBusy(false);
    const initialType = initial?.type ? typesByKey[initial.type] : null;
    if (initial && initialType) {
      setCategory(initialType.category);
      setTypeKey(initialType.key);
      setForm(formFromRule(initialType, initial.rule || initial.draft));
      setInPlace(
        mode === MODES.REPLACE &&
          Boolean(initial.ruleId) &&
          (initial.usageCount || 0) <= 1,
      );
    } else if (!initial) {
      setCategory(null);
      setTypeKey(null);
      setForm(emptyForm(null));
      setInPlace(false);
    }
  }, [open, initial, mode, typesByKey]);

  const clientCheck = useMemo(
    () => validateForm(type, form, totalDives),
    [type, form, totalDives],
  );
  const complete = isFormComplete(type, form);
  const draft = useMemo(() => draftFromForm(type, form), [type, form]);

  // Debounced preview: text + field errors + "already exists".
  useEffect(() => {
    if (
      !open ||
      !type ||
      !complete ||
      Object.keys(clientCheck.errors).length > 0
    ) {
      setPreview(IDLE_PREVIEW);
      return undefined;
    }
    const id = requestId.current + 1;
    requestId.current = id;
    setPreview((prev) => ({ ...prev, loading: true, error: null }));
    const handle = setTimeout(() => {
      rulesApi
        .preview(draft)
        .then((data) => {
          if (requestId.current === id)
            setPreview({ data, loading: false, error: null });
        })
        .catch((e) => {
          if (requestId.current === id) {
            setPreview({ data: null, loading: false, error: e.message });
          }
        });
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(handle);
    // draft is derived from type+form; clientCheck from the same inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, form, complete]);

  const serverErrors = useMemo(() => {
    const byField = {};
    const general = [];
    (preview.data?.errors || []).forEach((err) => {
      if (err.field && !["rule", "type"].includes(err.field)) {
        byField[err.field] = err.message;
      } else {
        general.push(err.message);
      }
    });
    return { byField, general };
  }, [preview.data]);

  const errors = { ...serverErrors.byField, ...clientCheck.errors };
  const valid =
    Boolean(preview.data?.valid) && Object.keys(errors).length === 0;
  const existingId = preview.data?.existingRuleId || null;
  const existingUsage = preview.data?.existingUsageCount || 0;
  const sameAsStored =
    Boolean(initial?.ruleId) && existingId === initial?.ruleId;

  const pickType = (key) => {
    setTypeKey(key);
    setForm(emptyForm(typesByKey[key]));
  };

  const result = (ruleId, text, usageCount) => ({
    ruleId,
    draft: ruleId ? null : draft,
    rule: null,
    type: type.key,
    category: type.category,
    text,
    usageCount,
  });

  const apply = async () => {
    if (!valid || !type) return;
    if (mode === MODES.EVERYWHERE) {
      await updateEverywhere(false);
      return;
    }
    if (mode === MODES.REPLACE && inPlace && initial?.ruleId && !sameAsStored) {
      setBusy(true);
      try {
        const dto = await rulesApi.updateRule(initial.ruleId, {
          ...draft,
          applyToAllSets: false,
        });
        onApply({ ...result(dto.id, dto.text, dto.usageCount), rule: dto });
      } catch (e) {
        if (e.status === 409 && e.details?.existingRuleId) {
          // Same values as another stored rule: link that one instead of editing.
          onApply(
            result(e.details.existingRuleId, preview.data.text, existingUsage),
          );
        } else {
          toast.error(e.message);
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    onApply(
      result(existingId, preview.data.text, existingId ? existingUsage : 0),
    );
  };

  const updateEverywhere = async (confirmed) => {
    setBusy(true);
    try {
      const dto = await rulesApi.updateRule(initial.ruleId, {
        ...draft,
        applyToAllSets: confirmed,
      });
      toast.success(`Rule ${dto.id} updated in every set that uses it`);
      onApply({
        ...result(dto.id, dto.text, dto.usageCount),
        rule: dto,
        everywhere: true,
      });
    } catch (e) {
      if (e.status === 409 && e.details?.affectedSets) {
        setAffected(e.details);
      } else if (e.status === 409 && e.details?.existingRuleId) {
        setAffected({
          existingRuleId: e.details.existingRuleId,
          message: e.details.message,
        });
      } else {
        toast.error(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const primaryLabel = () => {
    if (mode === MODES.EVERYWHERE) return "Update everywhere";
    if (mode === MODES.REPLACE) {
      if (sameAsStored) return "Keep rule";
      if (inPlace && initial?.ruleId)
        return `Save changes to rule #${initial.ruleId}`;
      return existingId
        ? `Use existing rule #${existingId}`
        : "Replace with new rule";
    }
    return existingId ? `Add existing rule #${existingId}` : "Create and add";
  };

  const title =
    mode === MODES.EVERYWHERE
      ? `Edit rule #${initial?.ruleId} everywhere`
      : mode === MODES.REPLACE
      ? `Edit rule in ${setName}`
      : `Add rule to ${setName}`;

  const step = !category ? 1 : !type ? 2 : 3;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => reason !== "backdropClick" && onClose()}
      scroll="body"
      maxWidth="md"
      fullWidth
      aria-labelledby="add-rule-title"
    >
      <DialogTitle disableTypography id="add-rule-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography component="h2" variant="h6">
            {title}
          </Typography>
          <IconButton
            className={classes.iconButton}
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {!locked && (
          <Box className={classes.steps} aria-hidden="true">
            <div className={step > 1 ? classes.stepDone : classes.stepOn}>
              {step > 1 ? "✓ " : ""}1 Kind of rule
            </div>
            <div
              className={
                step > 2 ? classes.stepDone : step === 2 ? classes.stepOn : ""
              }
            >
              {step > 2 ? "✓ " : ""}2 Rule type
            </div>
            <div className={step === 3 ? classes.stepOn : ""}>
              3 Scope &amp; values
            </div>
          </Box>
        )}

        {catalog.error && <Alert severity="error">{catalog.error}</Alert>}
        {catalog.loading && !catalog.types.length && (
          <Box className={classes.progressWrap}>
            <CircularProgress aria-label="Loading rule catalog" />
          </Box>
        )}

        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          {!locked && catalog.types.length > 0 && (
            <Box>
              <Typography id="kind-label" className={classes.fieldGroupLabel}>
                Kind of rule
              </Typography>
              <Box
                className={classes.categoryChips}
                role="group"
                aria-labelledby="kind-label"
              >
                {RULE_CATEGORY_ORDER.map((key) => (
                  <Chip
                    key={key}
                    label={RULE_CATEGORY_LABELS[key]}
                    clickable
                    color={category === key ? "primary" : "default"}
                    variant={category === key ? "default" : "outlined"}
                    className={classes.categoryChip}
                    component="button"
                    type="button"
                    aria-pressed={category === key}
                    onClick={() => {
                      setCategory(key);
                      setTypeKey(null);
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {!locked && category && (
            <Box>
              <Typography id="type-label" className={classes.fieldGroupLabel}>
                Rule type
              </Typography>
              <Box
                className={classes.typeGrid}
                role="group"
                aria-labelledby="type-label"
              >
                {catalog.types
                  .filter((t) => t.category === category && !t.pinned)
                  .map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={classes.typeCard}
                      aria-pressed={typeKey === t.key}
                      onClick={() => pickType(t.key)}
                      title={t.description}
                    >
                      {t.label}
                      <small>
                        {t.key}
                        {t.mutates
                          ? " · changes the DD instead of rejecting"
                          : ""}
                      </small>
                    </button>
                  ))}
              </Box>
            </Box>
          )}

          {type && (
            <Box>
              {locked && (
                <Typography
                  className={classes.muted}
                  style={{ marginBottom: 8 }}
                >
                  {typeLabel(type)} ({type.key}) — {type.description}
                </Typography>
              )}
              {!locked && (
                <Typography
                  className={classes.muted}
                  style={{ marginBottom: 8 }}
                >
                  {type.description}
                </Typography>
              )}
              <RuleFieldsForm
                type={type}
                form={form}
                onChange={setForm}
                categories={catalog.categories}
                heights={catalog.heights}
                fullCodeModes={catalog.fullCodeModes}
                errors={errors}
                disabled={busy}
              />
            </Box>
          )}

          {type && (
            <Box className={classes.preview} aria-live="polite">
              <div className={classes.previewLabel}>
                Preview (from the server){preview.loading ? " …" : ""}
              </div>
              {preview.data?.text && <div>{preview.data.text}</div>}
              {!preview.data && !preview.loading && !preview.error && (
                <span className={classes.muted}>
                  {complete
                    ? "Checking…"
                    : "Fill in the fields to see the rule text."}
                </span>
              )}
              {preview.error && (
                <span style={{ color: "#b3261e" }}>{preview.error}</span>
              )}
              {serverErrors.general.map((message) => (
                <div key={message} style={{ color: "#b3261e" }}>
                  {message}
                </div>
              ))}
            </Box>
          )}

          {clientCheck.warnings.map((warning) => (
            <Alert key={warning} severity="warning">
              {warning}
            </Alert>
          ))}

          {type && mode !== MODES.EVERYWHERE && existingId && !sameAsStored && (
            <Box className={classes.banner} role="status">
              <span aria-hidden="true">⚠</span>
              <div>
                <strong>
                  This rule already exists (#{existingId}) and is used by{" "}
                  {existingUsage} rule set
                  {existingUsage === 1 ? "" : "s"}.
                </strong>
                <div>
                  It will be reused, not created again. Change a value to make a
                  different rule.
                </div>
              </div>
            </Box>
          )}

          {type &&
            mode === MODES.REPLACE &&
            initial?.ruleId &&
            (initial.usageCount || 0) <= 1 && (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={inPlace}
                    onChange={(e) => setInPlace(e.target.checked)}
                    disabled={busy || sameAsStored}
                  />
                }
                label={`Also update rule #${initial.ruleId} itself (it is used only by this set)`}
              />
            )}

          {affected?.affectedSets && (
            <Alert severity="warning">
              <strong>
                Updating rule #{initial?.ruleId} changes{" "}
                {affected.affectedSets.length} rule set
                {affected.affectedSets.length === 1 ? "" : "s"}:
              </strong>
              <ul style={{ margin: "4px 0 8px", paddingLeft: 20 }}>
                {affected.affectedSets.map((s) => (
                  <li key={s.ruleSetId}>
                    {s.ruleName} ({s.ruleGroupName}
                    {s.status === "DEPRECATED" ? ", deprecated" : ""})
                  </li>
                ))}
              </ul>
              <Button
                variant="contained"
                color="primary"
                className={classes.touch}
                disabled={busy}
                onClick={() => updateEverywhere(true)}
              >
                Update all {affected.affectedSets.length}
              </Button>
            </Alert>
          )}
          {affected?.existingRuleId && (
            <Alert severity="error">
              {affected.message ||
                `A rule with these values already exists (#${affected.existingRuleId}).`}{" "}
              Replace it in this set instead of editing everywhere.
            </Alert>
          )}
        </Box>

        <Box className={classes.dialogFooter}>
          <Box className={classes.dialogFooterGroup}>
            {!locked && step > 1 && (
              <Button
                className={classes.touch}
                disabled={busy}
                onClick={() => (type ? setTypeKey(null) : setCategory(null))}
              >
                ‹ Back
              </Button>
            )}
          </Box>
          <Box className={classes.dialogFooterGroup}>
            <Button className={classes.touch} onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.touch}
              disabled={!valid || busy || preview.loading || Boolean(affected)}
              onClick={apply}
            >
              {primaryLabel()}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

AddRuleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(Object.values(MODES)),
  initial: PropTypes.shape({
    ruleId: PropTypes.number,
    type: PropTypes.string,
    draft: PropTypes.object,
    rule: PropTypes.object,
    usageCount: PropTypes.number,
  }),
  setName: PropTypes.string,
  totalDives: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

AddRuleDialog.defaultProps = {
  mode: MODES.ADD,
  initial: null,
  setName: "this rule set",
  totalDives: 0,
};

export default AddRuleDialog;
